import { useState } from "react";
import { useProjectStore } from "../state/projectStore";
import { useEditorStore } from "../state/editorStore";
import { usePlaybackStore } from "../state/playbackStore";
import { useEditor } from "../hooks/useEditor";
import { Fretboard } from "./Fretboard";
import { SelectIcon, PlusIcon, TrashIcon, ResetIcon, CheckIcon, FxIcon, RangeIcon, CopyIcon, CutIcon, PasteIcon } from "./icons";
import { TechIcon } from "./techIcons";
import { incompleteMeasures } from "../core/editor/validate";
import { userBeats, rawToBeatIndex } from "../core/notation/beatGrouping";
import { useT } from "../i18n";
import type { NoteDuration, TechniqueId } from "../types/domain";
import { fxList } from "../types/domain";

const DURS: NoteDuration[] = [1, 2, 4, 8, 16, 32];   // whole → 32nd

// the toggleable techniques, grouped for the 주법 palette (bup/bdn are placement-level, omitted here)
const FX_GROUPS: { key: string; items: TechniqueId[] }[] = [
  { key: "fxgrp.bend", items: ["b", "bh", "bq", "br"] },
  { key: "fxgrp.slide", items: ["sl", "ss", "sib", "sia", "sou", "sod"] },
  { key: "fxgrp.tech", items: ["h", "v", "vw", "tr", "tt", "tp"] },
  { key: "fxgrp.nuance", items: ["pm", "lr", "st", "ac", "x"] },
  { key: "fxgrp.harmonics", items: ["nh", "ah", "ph"] },
];

export function EditorDock() {
  const ed = useEditor();
  const t = useT();
  const insertArmed = useEditorStore((s) => s.insertArmed);
  const curDur = useEditorStore((s) => s.curDur);
  const curDotted = useEditorStore((s) => s.curDotted);
  const curTriplet = useEditorStore((s) => s.curTriplet);
  const curFx = useEditorStore((s) => s.curFx);
  const curTie = useEditorStore((s) => s.curTie);
  const curGrace = useEditorStore((s) => s.curGrace);
  const curRestDotted = useEditorStore((s) => s.curRestDotted);
  const rangeMode = useEditorStore((s) => s.rangeMode);
  const rangeAnchor = useEditorStore((s) => s.rangeAnchor);
  const rangeEnd = useEditorStore((s) => s.rangeEnd);
  const clipboard = useEditorStore((s) => s.clipboard);
  const restDur = useEditorStore((s) => s.restDur);
  const activeMeasure = useEditorStore((s) => s.activeMeasure);
  const selectedNoteIdx = useEditorStore((s) => s.selectedNoteIdx);
  const arrangement = useProjectStore((s) => s.arrangement);
  const beatLevelMul = useProjectStore((s) => s.beatLevelMul);
  const headerTempo = useProjectStore((s) => s.headerTempo);
  const isPlaying = usePlaybackStore((s) => s.isPlaying);

  const [pal, setPal] = useState<"note" | "rest" | "fx" | "adv" | null>(null);
  const [chordText, setChordText] = useState("");

  const chartMeasures = useProjectStore((s) => s.chartMeasures);
  const notesByMeasure: Record<number, typeof arrangement.measures[number]["notes"]> = {};
  for (const k in arrangement.measures) { const n = arrangement.measures[+k].notes; if (n) notesByMeasure[+k] = n; }
  const incomplete = [...incompleteMeasures(notesByMeasure as any,
    (mi) => arrangement.measures[mi]?.timeSignature?.[0] ?? chartMeasures[mi]?.beats.length ?? 4)].sort((a, b) => a - b);

  const measureNotes = activeMeasure >= 0 ? arrangement.measures[activeMeasure]?.notes : undefined;
  const sel = (activeMeasure >= 0 && selectedNoteIdx >= 0) ? measureNotes?.[selectedNoteIdx] : undefined;
  const noteDur: NoteDuration = sel && !sel.rest ? sel.dur : curDur;
  const restActiveDur: NoteDuration = sel && sel.rest ? sel.dur : restDur;
  const isNote = !!sel && !sel.rest;
  // in 추가 mode the toggles reflect the PEN (next note); in 선택 mode they reflect the selected note
  const noteDotted = insertArmed ? curDotted : (isNote ? !!sel!.dotted : curDotted);
  const noteTriplet = insertArmed ? curTriplet : (isNote ? !!sel!.triplet : curTriplet);
  const fxOn = (fx: TechniqueId) => (insertArmed ? curFx : fxList(sel?.fx)).includes(fx);
  const tieOn = insertArmed ? curTie : !!sel?.tie;
  const graceHOn = insertArmed ? curGrace === "h" : (!!sel?.grace && !sel?.graceSlide);
  const graceSlOn = insertArmed ? curGrace === "sl" : (!!sel?.grace && !!sel?.graceSlide);
  const restDotted = (sel && sel.rest) ? !!sel.dotted : curRestDotted;
  const hasRange = rangeMode && rangeAnchor != null && rangeEnd != null;

  // chord label of the selected beat (carried by the beat group's first item)
  const beatChordLabel = (() => {
    if (!sel || !measureNotes) return undefined;
    const bi = rawToBeatIndex(measureNotes, selectedNoteIdx);
    const g = bi >= 0 ? userBeats(measureNotes)[bi] : undefined;
    return g ? g.items[0].n.chordLabel : sel.chordLabel;
  })();

  // open advanced → prefill the chord field with the selected beat's current label
  const openAdv = () => { setChordText(beatChordLabel ?? ""); setPal((p) => (p === "adv" ? null : "adv")); };
  const commitChord = () => { ed.setChord(chordText); setPal(null); };

  const bumpLevel = (factor: number) => {
    const next = Math.min(2, Math.max(0.5, beatLevelMul * factor)) as 0.5 | 1 | 2;
    if (next !== beatLevelMul) ed.setBeatLevel(next);
  };
  const onReset = () => { setPal(null); if (confirm(t("ed.reset_confirm"))) ed.resetScore(); };

  return (
    <div className={"editor-dock" + (isPlaying ? " locked" : "")}>
      {/* note-length palette appears above the toolbar */}
      {pal === "note" && (
        <div className="pen-palette">
          {DURS.map((d) => (
            <button key={d} className={"pen-opt" + (noteDur === d ? " on" : "")} onClick={() => { ed.setDur(d); setPal(null); }} title={t("dur.n." + d)}>
              <span className="note-gly" data-d={d} />
            </button>
          ))}
          <span className="pen-div" />
          {/* 점음표(반박 추가) toggle — stays open so you can set length + dot together */}
          <button className={"pen-opt dot-opt" + (noteDotted ? " on" : "")} onClick={() => ed.toggleDot()} title={t("ed.dot")} aria-label={t("ed.dot")}>
            <span className="note-gly" data-d={noteDur} /><span className="dot-mark">.</span>
          </button>
          {/* 3연음(triplet) toggle */}
          <button className={"pen-opt tri-opt" + (noteTriplet ? " on" : "")} onClick={() => ed.toggleTriplet()} title={t("ed.triplet")} aria-label={t("ed.triplet")}>
            <span className="tri-mark">3</span><span className="note-gly" data-d={noteDur} />
          </button>
        </div>
      )}
      {pal === "rest" && (
        <div className="pen-palette">
          {DURS.map((d) => (
            <button key={d} className={"pen-opt" + (restActiveDur === d ? " on" : "")} onClick={() => { ed.placeRest(d); setPal(null); }} title={t("dur.r." + d)}>
              <span className="rest-gly" data-rd={d} />
            </button>
          ))}
          <span className="pen-div" />
          {/* 점쉼표(반박 추가) toggle — stays open so you can set length + dot together */}
          <button className={"pen-opt dot-opt" + (restDotted ? " on" : "")} onClick={() => ed.toggleRestDot()} title={t("ed.dot")} aria-label={t("ed.dot")}>
            <span className="rest-gly" data-rd={restActiveDur} /><span className="dot-mark">.</span>
          </button>
        </div>
      )}
      {pal === "fx" && (
        <div className="fx-palette">
          {/* 이음줄(tie) + 꾸밈음(grace) — NoteEvent flags, not fx, so they toggle on their own */}
          <div>
            <div className="fx-cat">{t("fxgrp.artic")}</div>
            <div className="fx-row">
              <button className={"fx-opt" + (tieOn ? " on" : "")} onClick={() => { ed.setTie(); setPal(null); }} title={t("fx.tie")}>
                <TechIcon fx="tie" /><span className="fx-lbl">{t("fx.tie")}</span>
              </button>
              <button className={"fx-opt" + (graceHOn ? " on" : "")} onClick={() => { ed.setGrace(false); setPal(null); }} title={t("fx.grh")}>
                <TechIcon fx="grh" /><span className="fx-lbl">{t("fx.grh")}</span>
              </button>
              <button className={"fx-opt" + (graceSlOn ? " on" : "")} onClick={() => { ed.setGrace(true); setPal(null); }} title={t("fx.grsl")}>
                <TechIcon fx="grsl" /><span className="fx-lbl">{t("fx.grsl")}</span>
              </button>
            </div>
          </div>
          {FX_GROUPS.map((grp) => (
            <div key={grp.key}>
              <div className="fx-cat">{t(grp.key)}</div>
              <div className="fx-row">
                {grp.items.map((fx) => (
                  <button key={fx} className={"fx-opt" + (fxOn(fx) ? " on" : "")} onClick={() => { ed.setFx(fx); setPal(null); }} title={t("fx." + fx)}>
                    <TechIcon fx={fx} /><span className="fx-lbl">{t("fx." + fx)}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="fx-hint">{t("ed.fx_hint")}</div>
        </div>
      )}
      {pal === "adv" && (
        <div className="adv-panel">
          <div className="adv-block">
            <div className="fx-cat">{t("ed.chord")}</div>
            <div className="adv-line">
              <input className="chord-input" value={chordText} placeholder={sel ? t("ed.chord_ph") : t("ed.chord_ph_nosel")}
                disabled={!sel} autoFocus={!!sel}
                onChange={(e) => setChordText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commitChord(); else if (e.key === "Escape") setPal(null); }} />
              <button className="pen-opt apply" onClick={commitChord} disabled={!sel} title={t("ed.chord_apply")}><CheckIcon size={16} /></button>
            </div>
          </div>
          <div className="adv-block">
            <div className="fx-cat">{t("ed.beatlevel")} <span className="eg-val">♩={headerTempo ?? "—"}{beatLevelMul !== 1 ? ` ×${beatLevelMul}` : ""}</span></div>
            <div className="adv-line">
              <button className="pen-opt" onClick={() => bumpLevel(0.5)} title={t("ed.div2")}>÷2</button>
              <button className="pen-opt" onClick={() => bumpLevel(2)} title={t("ed.mul2")}>×2</button>
              <button className="pen-opt reset" onClick={onReset} title={t("ed.reset_tip")}><ResetIcon size={15} /> {t("ed.reset")}</button>
            </div>
          </div>
        </div>
      )}
      <div className="editor-bar">
        <div className="eg">
          <span className="eg-label">{t("ed.input")}</span>
          <button className={"tool" + (!insertArmed && !rangeMode ? " on-sel" : "")} onClick={() => ed.setMode(false)} title={t("ed.select")}><SelectIcon size={15} /></button>
          <button className={"tool" + (insertArmed && !rangeMode ? " on-add" : "")} onClick={() => ed.setMode(true)} title={t("ed.add")}><PlusIcon size={16} /></button>
          <button className={"tool" + (rangeMode ? " on" : "")} onClick={() => { setPal(null); ed.setRangeMode(!rangeMode); }} title={t("ed.range")}><RangeIcon size={15} /></button>
        </div>

        <div className="eg">
          <span className="eg-label">{t("ed.note")}</span>
          <button className={"tool gly" + (pal === "note" ? " on" : "") + (noteDotted ? " dotted" : "")} onClick={() => setPal(pal === "note" ? null : "note")} disabled={rangeMode} title={t("ed.note_len")}>
            {noteTriplet && <span className="tri-mark sm">3</span>}<span className="note-gly" data-d={noteDur} />{noteDotted && <span className="dot-mark">.</span>} <span className="caret">▾</span>
          </button>
        </div>

        <div className="eg">
          <span className="eg-label">{t("ed.rest")}</span>
          <button className={"tool gly" + (pal === "rest" ? " on" : "")} onClick={() => setPal(pal === "rest" ? null : "rest")} disabled={rangeMode} title={t("ed.rest_len")}>
            <span className="rest-gly" data-rd={restActiveDur} />{restDotted && <span className="dot-mark">.</span>} <span className="caret">▾</span>
          </button>
        </div>

        <div className="eg">
          <span className="eg-label">{t("ed.fx")}</span>
          <button className={"tool" + (pal === "fx" ? " on" : "")} onClick={() => setPal(pal === "fx" ? null : "fx")} disabled={rangeMode || (!isNote && !insertArmed)} title={t("ed.fx_tip")}>
            <FxIcon size={16} /> <span className="caret">▾</span>
          </button>
        </div>

        <div className="eg">
          <span className="eg-label">{t("ed.editg")}</span>
          <button className="tool" onClick={() => ed.deleteSelected()} disabled={rangeMode || selectedNoteIdx < 0} title={t("ed.delete")}><TrashIcon size={15} /></button>
          <button className={"tool" + (pal === "adv" ? " on" : "")} onClick={openAdv} disabled={rangeMode} title={t("ed.adv")}><PlusIcon size={16} /></button>
        </div>

        {(rangeMode || clipboard) && (
          <div className="eg">
            <span className="eg-label">{t("ed.clip")}</span>
            <button className="tool" onClick={() => ed.copyRange()} disabled={!hasRange} title={t("ed.copy")}><CopyIcon size={15} /></button>
            <button className="tool" onClick={() => ed.cutRange()} disabled={!hasRange} title={t("ed.cut")}><CutIcon size={15} /></button>
            <button className="tool" onClick={() => ed.paste()} disabled={!clipboard} title={t("ed.paste")}><PasteIcon size={15} /></button>
          </div>
        )}

        <button className="tool done" onClick={() => ed.exitEditMode()} title={t("ed.done")} aria-label={t("ed.done")}><CheckIcon size={16} /></button>
      </div>

      {incomplete.length > 0 && (
        <div className="editor-warn">{t("ed.warn", { m: incomplete.map((m) => m + 1).join(", ") })}</div>
      )}

      {/* selected-note inspector (range mode shows its own hint) */}
      {rangeMode ? (
        <div className="editor-hint">
          {hasRange
            ? t("ed.range_sel", { m: (Math.min(rangeAnchor!.m, rangeEnd!.m) + 1) + (rangeAnchor!.m === rangeEnd!.m ? "" : "–" + (Math.max(rangeAnchor!.m, rangeEnd!.m) + 1)) })
            : t("ed.range_hint")}
        </div>
      ) : sel ? (
        <div className="editor-sel">
          {sel.rest ? t("ed.sel_rest") : t("ed.sel_pos", { s: sel.string ?? 0, f: sel.fret ?? 0 })}
          <span className="sel-sep">·</span>{sel.triplet ? "3:" : ""}{t("dur.n." + sel.dur)}{sel.dotted ? "." : ""}
          {isNote && fxList(sel.fx).map((f) => (<span key={f}><span className="sel-sep">·</span><b>{t("fx." + f)}</b></span>))}
          {isNote && sel.tie && (<><span className="sel-sep">·</span><b>{t("fx.tie")}</b></>)}
          {isNote && sel.grace && (<><span className="sel-sep">·</span><b>{t(sel.graceSlide ? "fx.grsl" : "fx.grh")}</b></>)}
          {beatChordLabel && (<><span className="sel-sep">·</span><span className="sel-chord">{beatChordLabel}</span></>)}
        </div>
      ) : (
        <div className="editor-hint">
          {activeMeasure < 0
            ? t("ed.hint_pick")
            : insertArmed
              ? t("ed.hint_add", { m: activeMeasure + 1 })
              : t("ed.hint_select", { m: activeMeasure + 1 })}
        </div>
      )}

      {!rangeMode && <Fretboard />}
    </div>
  );
}
